import {useCallback, useEffect, useRef, useState} from 'react';
import {DEFAULT_SPREADSHEET_MODEL_ID, getSpreadsheetModelById} from '../../helper/aiEnrichment';
import {fetchWorkspaceQuote, needsPaidRunReview} from './gridPaidRun';

/** Keep the reviewed grid scope frozen until its confirmed submission starts. */
export default function useSearchAiEnrichmentReview({
  selectedRows, unavailableIds, query, model, searchContext,
  closeActions, setSnackbar, runPaidAction, runEnrichmentAction, countDataUnits = (_, ids) => ids.length,
}) {
  const [review, setReview] = useState(null);
  const detailsPending = useRef(null);
  const settleDetails = useCallback((error) => {
    const pending = detailsPending.current;
    if (!pending || pending.settled) return;
    pending.settled = true;
    if (error) pending.onCompanyStatus?.({actionType: 'ai_enrichment', status: 'failed', error});
    pending.resolve();
  }, []);
  const close = useCallback(() => {
    settleDetails('Review cancelled. No credits were charged.');
    detailsPending.current = null;
    setReview(null);
  }, [settleDetails]);
  useEffect(() => () => settleDetails(detailsPending.current?.submitting
    ? 'Submission status is unknown. Check Saved Search history before retrying.'
    : 'Review cancelled. No credits were charged.'), [settleDetails]);
  const eligibleIds = () => Array.from(selectedRows).filter((id) => id && !unavailableIds.has(id));

  const start = (options = {}) => {
    const companyIds = options.companyIds || eligibleIds();
    const trimmedQuery = String(options.query ?? query).trim();
    const runModel = options.model || model;
    if (!companyIds.length || !trimmedQuery) {
      setSnackbar({open: true, message: !companyIds.length
        ? 'Select at least one company' : 'Please enter an AI prompt', severity: 'warning'});
      return false;
    }
    if (searchContext?.isDirty) {
      setSnackbar({open: true, message: 'Save the changed search before charging for AI enrichment.', severity: 'warning'});
      settleDetails('Save the changed search before charging for AI enrichment.');
      return false;
    }
    closeActions();
    setReview({
      companyIds: [...companyIds], query: trimmedQuery, model: runModel,
      provider: getSpreadsheetModelById(runModel).provider,
      workspaceId: searchContext?.workspaceId || null,
      ...(options.onCompanyStatus ? {onCompanyStatus: options.onCompanyStatus} : {}),
    });
    return true;
  };

  // A single saved-search row is quoted and attached silently: price on the button, no dialog.
  const attachSavedSingleRow = async ({companyIds, workspaceId, runModel, options}) => {
    const {quote, error} = await fetchWorkspaceQuote({
      workspaceId, companyIds, provider: getSpreadsheetModelById(runModel).provider, model: runModel,
    });
    if (error) {
      setSnackbar({open: true, message: error, severity: 'error', persist: true});
      options.onCompanyStatus?.({actionType: 'ai_enrichment', status: 'failed', error});
      return undefined;
    }
    return runEnrichmentAction('ai_enrichment', {
      ...options, companyIds, model: runModel, workspaceQuote: {workspaceId, quoteToken: quote.quote_token},
    });
  };
  const runSavedSingleRow = (companyIds, workspaceId) => runPaidAction(async () => {
    closeActions();
    return attachSavedSingleRow({companyIds, workspaceId, runModel: model, options: {query: query.trim()}});
  });

  /** Search Details reviews the saved scope; Saved Search Details keeps its single-row rule. */
  const runDetailsAction = (actionType, options = {}) => {
    const companyIds = options.companyIds || [];
    const workspaceId = searchContext?.workspaceId;
    const attach = actionType === 'ai_enrichment' && companyIds.length === 1 && workspaceId
      && String(options.query || '').trim();
    if (!attach) return runEnrichmentAction(actionType, options);
    const runModel = String(options.model || DEFAULT_SPREADSHEET_MODEL_ID).trim();
    if (needsPaidRunReview(1, searchContext)) {
      if (detailsPending.current) {
        if (detailsPending.current.settled) options.onCompanyStatus?.({actionType: 'ai_enrichment', status: 'failed',
          error: 'An enrichment review is already open. Retry its quote or cancel it before starting another.'});
        return detailsPending.current.promise;
      }
      let resolve;
      const promise = new Promise((done) => { resolve = done; });
      detailsPending.current = {promise, resolve, onCompanyStatus: options.onCompanyStatus, settled: false};
      if (!start({...options, model: runModel})) {
        settleDetails('Review could not start. No credits were charged.');
        detailsPending.current = null;
      }
      return promise;
    }
    return attachSavedSingleRow({companyIds, workspaceId, runModel, options: {...options, query: options.query.trim()}});
  };

  /** Generate from the grid: review first unless it is a single-row run (owner ruling 2026-09-30). */
  const request = () => {
    const companyIds = eligibleIds();
    if (needsPaidRunReview(companyIds.length, searchContext)) return start();
    if (companyIds.length === 1 && query.trim() && searchContext?.workspaceId) {
      return runSavedSingleRow(companyIds, searchContext.workspaceId);
    }
    return runPaidAction(() => runEnrichmentAction('ai_enrichment', {query, model}));
  };

  /** Data Enrichment Continue: the same rule, priced from the client upper bound. */
  const requestData = (actionId) => {
    const companyIds = eligibleIds();
    if (!needsPaidRunReview(companyIds.length, null)) return runPaidAction(() => runEnrichmentAction(actionId));
    closeActions();
    setReview({kind: 'data', actionId, companyIds, workspaceId: null, unitCount: countDataUnits(actionId, companyIds)});
    return undefined;
  };

  const confirm = async (frozen, quote) => {
    if (frozen.kind === 'data') {
      return runPaidAction(() => runEnrichmentAction(frozen.actionId, {companyIds: frozen.companyIds}));
    }
    if (searchContext?.isDirty || (searchContext?.workspaceId || null) !== frozen.workspaceId) {
      setSnackbar({open: true, message: 'The active search changed. Review this enrichment again before charging.', severity: 'warning'});
      settleDetails('The active search changed. Review this enrichment again before charging.');
      close();
      return undefined;
    }
    let started = false;
    try {
      const outcome = await runPaidAction(async () => {
        started = true;
        if (detailsPending.current) Object.assign(detailsPending.current, {submitting: true, settled: false});
        try {
          return await runEnrichmentAction('ai_enrichment', {
            companyIds: frozen.companyIds, query: frozen.query, model: frozen.model,
            ...(frozen.onCompanyStatus ? {onCompanyStatus: frozen.onCompanyStatus} : {}),
            ...(quote ? {workspaceQuote: {workspaceId: frozen.workspaceId, quoteToken: quote.quote_token}} : {}),
          });
        } catch (error) {
          settleDetails('Submission status is unknown. Check Saved Search history before retrying.');
          throw error;
        }
      });
      settleDetails(started ? undefined : 'Another enrichment is still submitting. Retry this review when it finishes.');
      if (!outcome?.requote) detailsPending.current = null;
      return outcome;
    } catch (error) {
      settleDetails('The enrichment could not start. Review it again before charging.');
      throw error;
    }
  };

  // FE #748: whether Generate / Continue will open a confirmation, by the same rule they run.
  const confirmFollows = {
    ai: needsPaidRunReview(eligibleIds().length, searchContext),
    data: needsPaidRunReview(eligibleIds().length, null),
  };
  return {review, start, request, requestData, close, confirm, runDetailsAction, onQuoteError: settleDetails,
    confirmFollows};
}
